import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { useDebounceText } from '@/formUi'
import { InfoOutlined } from '@mui/icons-material'
import { Box, FormControl, FormHelperText, Link, Stack, TextField, Alert } from '@mui/material'

import { Tooltip } from '@/components/primitives/Tooltip'
import { HotwordsMenu } from './components/HotwordsMenu/HotwordsMenu'

import type { Notifications } from '../../../../types'
import { NotificationLimits } from './types'

import { useComputeSegmentsCount } from '@/hooks'
import { useHotwords } from './hooks/useHotwords'
import { useMessageLength } from './hooks/useMessageLength'
import { useRows } from './hooks/useRows'
import { useTexts } from './hooks/useTexts'

export type InputValue = string
const defaultComponentValue: InputValue = ''

type InputProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = InputValue,
> = {
  label: string
  testId?: string
  helperText?: string
  notificationType: Notifications
  notificationLimits: NotificationLimits
  includeUnsubscriptionInstruction?: boolean
  includeTrackingLink?: boolean
  notificationWay: 'PHONE' | 'EMAIL'
} & TidalFormBridgeOptions<InputValue, TargetField, State, AcceptedFormValue>

export function HotwordsField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = InputValue,
>(props: InputProps<TargetField, State, AcceptedFormValue>) {
  const {
    label,
    helperText,
    notificationType,
    notificationLimits,
    includeUnsubscriptionInstruction,
    includeTrackingLink,
    testId,
    notificationWay,
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      errorText,
      field: { visible, value: fieldData, status, required, disabled },
      onChange,
      onBlur,
      onFocus,
    },
  } = useTidalFormBridge<InputValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const fieldValue = fieldData as string

  const [value, change] = useDebounceText(fieldValue, onChange, 200)

  const texts = useTexts()

  const { hotwords, addHotword } = useHotwords(notificationType, fieldValue, onChange)

  const messageLength = useMessageLength(
    notificationLimits,
    fieldValue,
    includeUnsubscriptionInstruction,
    includeTrackingLink,
  )

  const segmentsCount = useComputeSegmentsCount(
    notificationLimits,
    fieldValue,
    includeUnsubscriptionInstruction,
    includeTrackingLink,
  )

  const rows = useRows()

  const fieldInvalid = status === 'invalid'
  const fieldIndeterminate = status === 'indeterminate'
  const fieldHasError = fieldInvalid || (fieldIndeterminate && errorText !== undefined)

  if (!visible) return null

  const info =
    notificationWay === 'PHONE' ? (
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" alignItems="center">
          {texts.segmentsCount(segmentsCount)}

          <Box ml={0.5} mt={0.5}>
            <Tooltip placement="bottom" title={texts.segmentsTooltip}>
              <InfoOutlined sx={{ fontSize: 18 }} />
            </Tooltip>
          </Box>
        </Stack>

        <Link
          href="https://workwave.my.site.com/routemanager/s/article/Enhanced-Communication-Plan-and-Usage"
          target="_blank"
          rel="noopener noreferrer"
          variant="body1"
        >
          {texts.learnMore}
        </Link>
      </Stack>
    ) : (
      `${messageLength}/2500`
    )

  return (
    <Stack data-testid={testId} data-trackid={testId} spacing={2}>
      <FormControl error={fieldHasError}>
        <TextField
          error={fieldHasError}
          required={required}
          label={label}
          multiline
          rows={rows}
          value={value}
          onChange={change}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
        />
        <FormHelperText sx={{ marginX: 0 }}>{info}</FormHelperText>
      </FormControl>
      {fieldHasError && <Alert severity="error">{errorText}</Alert>}
      <Stack direction="column">
        <HotwordsMenu addHotword={addHotword} hotwords={hotwords} disabled={disabled} />
        {notificationWay === 'PHONE' && <FormHelperText>{texts.info}</FormHelperText>}
      </Stack>
    </Stack>
  )
}
